import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import LangSwitcher from "@/components/LangSwitcher";
import MobileMenu from "./MobileMenu";

/**
 * Floating pill-shaped glass header inspired by luxury SaaS references.
 * — Full-width transparent bar
 * — Inner content is a floating pill with heavy backdrop-blur, thin bevel highlight
 * — Grows / shrinks slightly on scroll for a "sinking" feel
 */
export default function VangardHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const nav = [
    { to: "/about", label: t("vc.nav.about") },
    { to: "/membership", label: t("vc.nav.membership") },
    { to: "/magazine", label: t("vc.nav.magazine") },
    { to: "/concierge", label: t("vc.nav.concierge") },
  ];
  const isActive = (to) => location.pathname === to;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "pt-3 pb-3" : "pt-6 pb-4"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className={`relative rounded-full backdrop-blur-2xl border border-white/[0.08] overflow-hidden ${
              scrolled
                ? "bg-[#0A0A0A]/85 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.9)]"
                : "bg-white/[0.03] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)]"
            } transition-all duration-500`}
          >
            {/* Top bevel highlight */}
            <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
            {/* Ambient inner glow */}
            <div className="pointer-events-none absolute -inset-px rounded-full [background:radial-gradient(60%_120%_at_50%_-20%,rgba(255,255,255,0.10),transparent_70%)]" />

            <div className="relative flex items-center gap-4 px-4 md:px-6 py-2.5">
              {/* Brand */}
              <Link
                to="/"
                className="flex items-center gap-2.5 shrink-0 pr-3 border-r border-white/[0.08]"
                data-testid="brand-mark"
              >
                <img
                  src="/logo-light.png"
                  alt=""
                  className="h-6 w-auto"
                  aria-hidden="true"
                  draggable={false}
                />
                <span className="font-serif text-[19px] leading-none tracking-tight text-white">
                  Vangard<span className="text-neutral-500">Club</span>
                </span>
              </Link>

              {/* Nav — desktop */}
              <nav className="hidden lg:flex items-center gap-1 pl-2">
                {nav.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`nav-${l.to.slice(1)}`}
                    className={`relative px-4 py-2 text-[11px] tracking-[0.25em] uppercase rounded-full transition-colors ${
                      isActive(l.to)
                        ? "text-white bg-white/[0.06]"
                        : "text-neutral-400 hover:text-white hover:bg-white/[0.03]"
                    }`}
                  >
                    {l.label}
                    {isActive(l.to) && (
                      <motion.span
                        layoutId="nav-dot"
                        className="absolute -bottom-[3px] left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-white"
                      />
                    )}
                  </Link>
                ))}
              </nav>

              <div className="flex-1" />

              {/* Right side */}
              <div className="hidden md:flex items-center gap-2">
                <LangSwitcher />
                {user ? (
                  <button
                    onClick={() => navigate("/account")}
                    className="inline-flex items-center gap-2 pl-4 pr-2 py-1.5 rounded-full bg-white text-black text-[11px] tracking-[0.2em] uppercase font-medium hover:bg-neutral-200 transition-colors"
                    data-testid="header-account"
                  >
                    {t("vc.nav.account")}
                    <span className="w-6 h-6 rounded-full bg-black text-white text-[10px] flex items-center justify-center font-medium">
                      {(user.name || "?").slice(0, 1).toUpperCase()}
                    </span>
                  </button>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="text-[11px] tracking-[0.25em] uppercase text-neutral-400 hover:text-white px-3 py-2 transition-colors"
                      data-testid="header-signin"
                    >
                      {t("vc.nav.signin")}
                    </Link>
                    <Link
                      to="/register"
                      className="inline-flex items-center px-5 py-2 rounded-full bg-white text-black text-[11px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors"
                      data-testid="header-join"
                    >
                      {t("vc.nav.join")}
                    </Link>
                  </>
                )}
              </div>

              {/* Burger — mobile */}
              <button
                onClick={() => setOpen(true)}
                aria-label="Menu"
                data-testid="mobile-menu-toggle"
                className="md:hidden ml-auto relative w-10 h-10 rounded-full border border-white/15 flex items-center justify-center"
              >
                <span className="block w-4 h-px bg-white before:content-[''] before:absolute before:w-4 before:h-px before:bg-white before:-translate-y-1.5 after:content-[''] after:absolute after:w-4 after:h-px after:bg-white after:translate-y-1.5" />
              </button>
            </div>
          </motion.div>
        </div>
      </header>

      <MobileMenu open={open} onClose={() => setOpen(false)} />
    </>
  );
}
